/**
 * Custom Hook: useForecastCopy
 *
 * A reusable React hook that provides copy functionality for forecast components.
 * This hook handles copying metric values from one month to multiple target months
 * (next month, quarter, or year) and recalculates all affected months.
 *
 * Usage:
 * ```tsx
 * const { handleCopyToNext } = useForecastCopy({
 *   config: FTD_CONFIG, // or PROFIT_CONFIG, GROWTH_CONFIG
 *   editableStates: { editableNRPU, editableGrowthTarget, ... },
 *   calculateValues: calculateValuesFunction,
 *   monthNames: ["January", "February", ...]
 * });
 * ```
 */

import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  copyMetricToMonths,
  getCurrentMetricKeyGeneric,
  CopyType,
  ComponentConfig,
} from "../components/AssetManagement/Overview/PerformanceForecaster/CreateNewForeCast/helpers/Forecastcopyhelper";

import { setMonthlyData } from "../features/forecastersSlice";

interface UseForecastCopyParams {
  /**
   * Component configuration defining editable fields and their mappings
   */
  config: ComponentConfig;

  /**
   * Object containing all editable state values
   * Key should match the stateGetter in config
   */
  editableStates: Record<string, string | null>;

  /**
   * Function to recalculate derived values for a month
   * @param month - Month name
   * @param data - Current month data
   * @param prevActiveUsers - Previous month's active users (optional)
   * @returns Updated month data with calculated values
   */
  calculateValues: (month: string, data: any, prevActiveUsers?: number) => any;

  /**
   * Array of month names in order
   */
  monthNames: string[];
}

interface UseForecastCopyReturn {
  /**
   * Handler function to copy the current metric to target months
   * @param copyType - Type of copy operation: "month", "quarter", or "year"
   */
  handleCopyToNext: (copyType: CopyType) => void;
}

/**
 * Custom hook for forecast copy functionality
 */
export const useForecastCopy = ({
  config,
  editableStates,
  calculateValues,
  monthNames,
}: UseForecastCopyParams): UseForecastCopyReturn => {
  const dispatch = useDispatch();
  const { monthlyData } = useSelector((state: any) => state?.forecasters);

  const handleCopyToNext = useCallback(
    (copyType: CopyType) => {
      // Get current metric info based on configuration
      const currentMetric = getCurrentMetricKeyGeneric(editableStates, config);

      if (!currentMetric) {
        console.error(`No metric is currently being edited in ${config.name}`);
        return;
      }

      const { month: sourceMonth, metricKey } = currentMetric;

      // Copy the metric to target months
      let updatedMonthlyData = copyMetricToMonths(
        monthlyData,
        sourceMonth,
        metricKey,
        copyType,
      );

      // Recalculate all affected months
      const sourceMonthIndex = monthNames.indexOf(sourceMonth);

      for (let i = sourceMonthIndex; i < monthNames.length; i++) {
        const currentMonth = monthNames[i];
        if (updatedMonthlyData[currentMonth]) {
          if (i === sourceMonthIndex) {
            // For the source month, recalculate with existing values
            updatedMonthlyData[currentMonth] = calculateValues(
              currentMonth,
              updatedMonthlyData[currentMonth],
            );
          } else {
            // For subsequent months, recalculate with previous month's active users
            const prevMonth = monthNames[i - 1];
            const prevMonthActiveUsers =
              updatedMonthlyData[prevMonth].activeUsers;
            updatedMonthlyData[currentMonth] = calculateValues(
              currentMonth,
              updatedMonthlyData[currentMonth],
              prevMonthActiveUsers,
            );
          }
        }
      }

      // Update the Redux store
      dispatch(setMonthlyData(updatedMonthlyData));
    },
    [
      config,
      editableStates,
      monthlyData,
      monthNames,
      calculateValues,
      dispatch,
    ],
  );

  return { handleCopyToNext };
};

/**
 * Type exports for external use
 */
export type { UseForecastCopyParams, UseForecastCopyReturn };
